import React from "react";
import {
  UserRole,
  UserWithRole,
  ROLE_CAPABILITIES,
  ROLE_ROUTE_ACCESS,
  Permission,
  Resource,
  Action,
  createPermission,
  RoleCapabilities,
} from "../types/user";

/**
 * Role-based utility functions for handling user permissions and access control
 * Updated to work with the new flexible permission system
 */

// Storage keys for localStorage
const USER_ROLE_STORAGE_KEY = "user_role_info";

/**
 * Save user role information to localStorage
 */
export const saveUserRoleToStorage = (
  userRole: UserRole,
  additionalData?: Partial<UserWithRole>,
): void => {
  try {
    const roleData = {
      role: userRole,
      timestamp: Date.now(),
      ...additionalData,
    };
    localStorage.setItem(USER_ROLE_STORAGE_KEY, JSON.stringify(roleData));
    console.log("🔐 User role saved to localStorage:", userRole);
  } catch (error) {
    console.error("❌ Error saving user role to localStorage:", error);
  }
};

/**
 * Get user role information from localStorage
 */
export const getUserRoleFromStorage = (): {
  role: UserRole;
  timestamp: number;
} | null => {
  try {
    const stored = localStorage.getItem(USER_ROLE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed;
    }
    return null;
  } catch (error) {
    console.error("❌ Error reading user role from localStorage:", error);
    return null;
  }
};

/**
 * Clear user role information from localStorage
 */
export const clearUserRoleFromStorage = (): void => {
  try {
    localStorage.removeItem(USER_ROLE_STORAGE_KEY);
    console.log("🗑️ User role cleared from localStorage");
  } catch (error) {
    console.error("❌ Error clearing user role from localStorage:", error);
  }
};

/**
 * Extract user role from Auth0 user object or localStorage
 */
export const extractUserRole = (user: any): UserRole | null => {
  // First, try to get role from user object (from backend/Auth0)
  if (user?.role && Object.values(UserRole).includes(user.role)) {
    return user.role as UserRole;
  }

  // If not found in user object, try localStorage as fallback
  const storedRole = getUserRoleFromStorage();
  if (storedRole?.role) {
    return storedRole.role;
  }

  console.warn("⚠️ No valid user role found in user object or localStorage");
  return null;
};

/**
 * Get role capabilities for a given role
 */
export const getRoleCapabilities = (userRole: UserRole): RoleCapabilities => {
  return ROLE_CAPABILITIES[userRole];
};

/**
 * Check if user has a specific permission using the new flexible system
 */
export const hasPermission = (
  userRole: UserRole | null,
  permission: Permission,
): boolean => {
  if (!userRole) {
    console.warn("⚠️ No user role provided for permission check");
    return false;
  }

  const capabilities = getRoleCapabilities(userRole);

  // If user has full access, allow everything
  if (capabilities.fullAccess) {
    return true;
  }

  // Check if permission is explicitly denied
  if (capabilities.deniedPermissions?.includes(permission)) {
    return false;
  }

  // Check if permission is explicitly granted
  if (capabilities.specificPermissions?.includes(permission)) {
    return true;
  }

  // Parse the permission to check resource and action
  const [resource, action] = permission.split(":") as [Resource, Action];

  // Check if user has access to the resource and can perform the action
  const hasResourceAccess = capabilities.resources.includes(resource);
  const hasActionAccess = capabilities.actions.includes(action);

  return hasResourceAccess && hasActionAccess;
};

/**
 * Check if user can perform a specific action on a resource
 */
export const canPerformAction = (
  userRole: UserRole | null,
  resource: Resource,
  action: Action,
): boolean => {
  if (!userRole) return false;

  const permission = createPermission(resource, action);
  return hasPermission(userRole, permission);
};

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (
  userRole: UserRole | null,
  route: string,
): boolean => {
  if (!userRole) {
    console.warn("⚠️ No user role provided for route access check");
    return false;
  }

  const allowedRoutes = ROLE_ROUTE_ACCESS[userRole];

  // Check exact match first
  if (allowedRoutes.includes(route)) {
    return true;
  }

  // Check if route starts with any allowed route (for dynamic routes like /dashboard/:slug)
  return allowedRoutes.some((allowedRoute) => {
    // Handle dynamic routes
    if (route.startsWith(allowedRoute)) {
      return true;
    }
    return false;
  });
};

/**
 * Get all accessible routes for a user role
 */
export const getRoleRoutes = (userRole: UserRole): string[] => {
  return [...ROLE_ROUTE_ACCESS[userRole]];
};

/**
 * Check if user is admin
 */
export const isAdmin = (userRole: UserRole | null): boolean => {
  return userRole === UserRole.ADMIN;
};

/**
 * Check if user is researcher
 */
export const isResearcher = (userRole: UserRole | null): boolean => {
  return userRole === UserRole.RESEARCHER;
};

/**
 * @deprecated Use isResearcher instead
 */
export const isEditor = (userRole: UserRole | null): boolean => {
  return userRole === UserRole.RESEARCHER;
};

/**
 * Check if user is client/VIEWER
 */
export const isClient = (userRole: UserRole | null): boolean => {
  return userRole === UserRole.CLIENT || userRole === UserRole.VIEWER;
};

/**
 * CRUD operation helpers using the new system
 */
export const canCreate = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canPerformAction(userRole, resource, "create");
};

export const canRead = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canPerformAction(userRole, resource, "read");
};

export const canUpdate = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canPerformAction(userRole, resource, "update");
};

export const canDelete = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canPerformAction(userRole, resource, "delete");
};

// Legacy aliases for backward compatibility
export const canEdit = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canUpdate(userRole, resource);
};

export const canView = (
  userRole: UserRole | null,
  resource: Resource,
): boolean => {
  return canRead(userRole, resource);
};

/**
 * Special capability checks
 */
export const canManageUsers = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const capabilities = getRoleCapabilities(userRole);
  return capabilities.canManageUsers || false;
};

export const canAccessSettings = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const capabilities = getRoleCapabilities(userRole);
  return capabilities.canAccessSettings || false;
};

export const canViewAuditLogs = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const capabilities = getRoleCapabilities(userRole);
  return capabilities.canViewAuditLogs || false;
};

export const canExportData = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const capabilities = getRoleCapabilities(userRole);
  return capabilities.canExportData || false;
};

export const canImportData = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const capabilities = getRoleCapabilities(userRole);
  return capabilities.canImportData || false;
};

/**
 * Get user role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  switch (role) {
    case UserRole.ADMIN:
      return "Administrator";
    case UserRole.RESEARCHER:
      return "Researcher";
    case UserRole.CLIENT:
      return "Client";
    case UserRole.VIEWER:
      return "VIEWER";
    default:
      return "Unknown";
  }
};

/**
 * Validate if a role string is a valid UserRole
 */
export const isValidRole = (role: string): role is UserRole => {
  return Object.values(UserRole).includes(role as UserRole);
};

/**
 * Get default route for user role (where to redirect after login)
 */
export const getDefaultRouteForRole = (userRole: UserRole): string => {
  switch (userRole) {
    case UserRole.ADMIN:
    case UserRole.RESEARCHER:
      return "/surveys"; // Full access users go to surveys
    case UserRole.CLIENT:
    case UserRole.VIEWER:
      return "/dashboard"; // View-only users go to dashboard
    default:
      return "/dashboard";
  }
};

/**
 * Get all resources a user can access
 */
export const getAccessibleResources = (
  userRole: UserRole | null,
): Resource[] => {
  if (!userRole) return [];

  const capabilities = getRoleCapabilities(userRole);

  if (capabilities.fullAccess) {
    // Return all possible resources if user has full access
    return [
      "dashboard",
      "survey",
      "dataset",
      "user",
      "settings",
      "report",
      "analytics",
      "export",
      "import",
      "template",
      "notification",
      "integration",
      "api",
      "audit",
      "backup",
    ];
  }

  return capabilities.resources;
};

/**
 * Get all actions a user can perform
 */
export const getAvailableActions = (userRole: UserRole | null): Action[] => {
  if (!userRole) return [];

  const capabilities = getRoleCapabilities(userRole);

  if (capabilities.fullAccess) {
    // Return all possible actions if user has full access
    return [
      "create",
      "read",
      "update",
      "delete",
      "share",
      "export",
      "import",
      "manage",
      "configure",
      "publish",
      "archive",
      "restore",
    ];
  }

  return capabilities.actions;
};

/**
 * Role-based wrapper function for conditional rendering
 */
export const withRoleCheck = <T extends {}>(
  userRole: UserRole | null,
  permission: Permission,
  component: React.ComponentType<T>,
  fallback?: React.ComponentType<T> | null,
) => {
  return (props: T) => {
    if (hasPermission(userRole, permission)) {
      const Component = component;
      return React.createElement(Component, props);
    }

    if (fallback) {
      const Fallback = fallback;
      return React.createElement(Fallback, props);
    }

    return null;
  };
};

/**
 * Hook for role-based conditional rendering in components
 */
export const useRoleCheck = (
  userRole: UserRole | null,
  permission: Permission,
): boolean => {
  return hasPermission(userRole, permission);
};

/**
 * Higher-order component for role-based access control
 */
export const withRoleAccess = <P extends {}>(
  Component: React.ComponentType<P>,
  requiredPermission: Permission,
  fallbackComponent?: React.ComponentType<P>,
) => {
  return (props: P & { userRole?: UserRole | null }) => {
    const { userRole, ...componentProps } = props;

    if (hasPermission(userRole || null, requiredPermission)) {
      return React.createElement(Component, componentProps as P);
    }

    if (fallbackComponent) {
      return React.createElement(fallbackComponent, componentProps as P);
    }

    return null;
  };
};
